import { useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Modal } from "antd";

import { searchController } from "../../../AppController";
import LoadingSkeleton from "../../../components/CustomComponents/LoadingSkeleton";
import { useAppSelector } from "../../../hooks/redux";
import { useAppNavigate } from "../../../hooks/router";
import styles from "./QuickSearchModal.module.scss";

interface QuickSearchModalProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY_FLOORS: number[] = [];

const displayName = (resident: ISearch["resident"]): string =>
  resident.name?.trim() || `${resident.last_name}, ${resident.first_name}`;

const initials = (resident: ISearch["resident"]): string =>
  `${resident.first_name?.[0] ?? ""}${
    resident.last_name?.[0] ?? ""
  }`.toUpperCase() || "?";

const QuickSearchModal = ({ open, onClose }: QuickSearchModalProps) => {
  const navigate = useAppNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { clinicId, selectedFloors } = useAppSelector((state) => ({
    clinicId: state.clinic.userClinic?.id,
    selectedFloors: state.clinic.selectedFloors ?? EMPTY_FLOORS,
  }));
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ISearch[]>([]);
  const tokens = useMemo(
    () =>
      query
        .trim()
        .toLowerCase()
        .split(/[\s,]+/)
        .filter(Boolean),
    [query]
  );

  useEffect(() => {
    if (!clinicId || tokens.length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    const backendTerm = [...tokens].sort((a, b) => b.length - a.length)[0];
    const timeout = window.setTimeout(async () => {
      try {
        const response = await searchController.getSearch({
          clinicId,
          floorIds: selectedFloors,
          search: backendTerm,
        });
        if (!active) return;
        const items =
          response.success && response.data ? response.data.data : [];
        setResults(
          items.filter(({ resident }) => {
            const name = `${resident.first_name} ${resident.last_name} ${
              resident.last_name
            } ${resident.first_name} ${resident.name ?? ""}`.toLowerCase();
            return tokens.every((token) => name.includes(token));
          })
        );
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [clinicId, selectedFloors, tokens]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      style={{ top: 88 }}
      styles={{ body: { padding: 0 } }}
      className={styles.modal}
      destroyOnHidden
      afterOpenChange={(isOpen) => {
        if (isOpen) inputRef.current?.focus();
      }}
    >
      <div className={styles.searchBar}>
        <SearchOutlined className={styles.searchBarIcon} />
        <input
          ref={inputRef}
          className={styles.searchBarInput}
          placeholder="Search patients by name…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search patients"
        />
      </div>
      <div className={styles.results}>
        {tokens.length === 0 ? (
          <div className={styles.hint}>Start typing a patient&apos;s name.</div>
        ) : loading ? (
          <LoadingSkeleton
            ariaLabel="Searching patients"
            compact
            rows={3}
            variant="list"
          />
        ) : !loading && results.length === 0 ? (
          <div className={styles.hint}>No matches for “{query.trim()}”.</div>
        ) : (
          results.map(({ resident, device }) => (
            <button
              key={resident.id}
              type="button"
              className={styles.resultRow}
              onClick={() => {
                onClose();
                navigate(`/patient-profile/${resident.id}` as never);
              }}
            >
              <span className={styles.avatar}>{initials(resident)}</span>
              <span className={styles.resultName}>{displayName(resident)}</span>
              {device?.name && (
                <span className={styles.resultMeta}>{device.name}</span>
              )}
            </button>
          ))
        )}
      </div>
    </Modal>
  );
};

export default QuickSearchModal;
